import { useMemo, useState } from "react"
import { Link, useNavigate } from "react-router"
import {
  SearchLg,
  Phone01,
  Mail01,
  ChevronRight,
  User01,
  Announcement01,
} from "@untitledui/icons"
import { motion } from "motion/react"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { ALL_ROLES } from "@/lib/team/roles"
import {
  useCustomerDirectory,
  type CustomerFollowUpState,
  type CustomerSummary,
} from "@/lib/customers/use-customer-profile"
import { PromoDialog } from "@/components/customers/promo-dialog"
import { formatMoney } from "@/lib/service-lane/baseline-math"
import { cx } from "@/lib/utils/cx"

// The "look up the customer" front door (Sam, Jul 10) — but shaped like a CRM,
// not a phone book: every person across both stores with WHERE THEY STAND
// (one derived follow-up state per person), what we've found for them, and
// when we last touched them. Segments slice the book by that state so "who
// still needs a confirm" or "who's mid follow-up" is one tap, not a hunt.

const STATE_META: Record<
  CustomerFollowUpState,
  { label: string; dot: string; text: string }
> = {
  confirm: {
    label: "Estimate to confirm",
    dot: "bg-amber-500",
    text: "text-amber-700 font-semibold",
  },
  no_contact: { label: "No contact info", dot: "bg-amber-500", text: "text-amber-700" },
  to_call: {
    label: "Waiting on a call",
    dot: "bg-teal-500",
    text: "text-teal-700 font-semibold",
  },
  following: { label: "Following up", dot: "bg-blue-500", text: "text-zinc-700" },
  booked: { label: "Booked", dot: "bg-sky-500", text: "text-zinc-700" },
  won: { label: "Won", dot: "bg-emerald-500", text: "text-zinc-700" },
  passed: { label: "Passed", dot: "bg-zinc-300", text: "text-zinc-500" },
}

type SegmentKey =
  | "all"
  | "confirm"
  | "to_call"
  | "following"
  | "closed"
  | "no_contact"
  | "passed"

const SEGMENTS: { key: SegmentKey; label: string; match: CustomerFollowUpState[] }[] = [
  { key: "all", label: "All", match: [] },
  { key: "confirm", label: "To confirm", match: ["confirm"] },
  { key: "to_call", label: "To call", match: ["to_call"] },
  { key: "following", label: "Following up", match: ["following"] },
  { key: "closed", label: "Booked & won", match: ["booked", "won"] },
  { key: "no_contact", label: "No contact info", match: ["no_contact"] },
  { key: "passed", label: "Passed", match: ["passed"] },
]

function relTime(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 60) return "Just now"
  const h = Math.round(mins / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  if (d < 7) return `${d}d ago`
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function initialsOf(name: string): string {
  return name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()
}

export default function CustomersPage() {
  const { customers, isLoading } = useCustomerDirectory()
  const [q, setQ] = useState("")
  const [segment, setSegment] = useState<SegmentKey>("all")
  const [promoOpen, setPromoOpen] = useState(false)

  const counts = useMemo(() => {
    const by: Record<SegmentKey, number> = {
      all: customers.length,
      confirm: 0,
      to_call: 0,
      following: 0,
      closed: 0,
      no_contact: 0,
      passed: 0,
    }
    for (const c of customers) {
      if (c.followUp === "confirm") by.confirm++
      else if (c.followUp === "to_call") by.to_call++
      else if (c.followUp === "following") by.following++
      else if (c.followUp === "booked" || c.followUp === "won") by.closed++
      else if (c.followUp === "no_contact") by.no_contact++
      else by.passed++
    }
    return by
  }, [customers])

  const totalIdentified = useMemo(
    () => customers.reduce((s, c) => s + c.totalIdentified, 0),
    [customers],
  )

  const filtered = useMemo(() => {
    const seg = SEGMENTS.find((s) => s.key === segment)!
    const term = q.trim().toLowerCase()
    // "5551234" must find "(555) 123-4567" — compare digits to digits (#58).
    const termDigits = term.replace(/\D/g, "")
    return customers.filter((c) => {
      if (segment !== "all" && !seg.match.includes(c.followUp)) return false
      if (!term) return true
      return (
        c.name.toLowerCase().includes(term) ||
        (c.phone ?? "").toLowerCase().includes(term) ||
        (termDigits.length >= 3 &&
          (c.phone ?? "").replace(/\D/g, "").includes(termDigits)) ||
        (c.email ?? "").toLowerCase().includes(term) ||
        c.vehicles.some((v) => v.toLowerCase().includes(term))
      )
    })
  }, [customers, q, segment])

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <div className="min-h-screen bg-zinc-50/40">
          {/* Same entrance as the customer card — the pages feel related (#65). */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-6xl px-4 md:px-8 py-6 md:py-10"
          >
            <header className="mb-5 flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 tracking-tight">
                  Customers
                </h1>
                {/* The book's worth, in one line — money framing, not row count. */}
                <p className="text-sm text-zinc-500 mt-0.5">
                  {isLoading ? (
                    "Loading your customers…"
                  ) : (
                    <>
                      {customers.length} customer{customers.length === 1 ? "" : "s"}
                      {totalIdentified > 0 && (
                        <>
                          {" · "}
                          <span className="font-semibold text-zinc-700 tabular-nums">
                            {formatMoney(totalIdentified)}
                          </span>{" "}
                          identified for them so far
                        </>
                      )}
                    </>
                  )}
                </p>
              </div>
              {customers.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPromoOpen(true)}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-zinc-900 text-white text-[13px] font-semibold hover:bg-black transition-colors"
                >
                  <Announcement01 className="size-4" aria-hidden />
                  Send a promotion
                </button>
              )}
            </header>

            {/* Search + state segments (zero-count segments stay hidden). */}
            <div className="mb-4 space-y-3">
              <div className="relative max-w-md">
                <SearchLg
                  className="size-4 text-zinc-400 absolute left-3.5 top-1/2 -translate-y-1/2"
                  aria-hidden
                />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  aria-label="Search customers"
                  placeholder="Search name, phone, email, or vehicle…"
                  className="w-full h-11 pl-10 pr-4 rounded-xl border border-zinc-200 bg-white text-[14px] text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-300"
                />
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {SEGMENTS.filter((s) => s.key === "all" || counts[s.key] > 0).map((s) => {
                  const active = segment === s.key
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setSegment(s.key)}
                      className={cx(
                        "h-8 px-3 rounded-lg text-[12.5px] font-semibold transition-colors inline-flex items-center gap-1.5",
                        active
                          ? "bg-zinc-900 text-white"
                          : "bg-white border border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:text-zinc-900",
                      )}
                    >
                      {s.label}
                      <span
                        className={cx(
                          "tabular-nums text-[11px]",
                          active ? "text-zinc-400" : "text-zinc-400",
                        )}
                      >
                        {counts[s.key]}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-2 animate-pulse">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-16 rounded-xl bg-white border border-zinc-200/70" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <Empty hasQuery={q.trim().length > 0 || segment !== "all"} />
            ) : (
              <>
                {/* Desktop — a real book: person · vehicles · where they stand ·
                    recency · what we found. One dot-state per row, no pill soup. */}
                <div className="hidden md:block rounded-2xl border border-zinc-200/80 bg-white shadow-sm overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-100 bg-zinc-50/60">
                        {[
                          { label: "Customer" },
                          { label: "Vehicles" },
                          { label: "Follow-up", width: "w-[180px]" },
                          { label: "Last activity", width: "w-[120px]" },
                          { label: "Found", width: "w-[110px]", right: true },
                          { label: "", width: "w-[44px]" },
                        ].map((col, i) => (
                          <th
                            key={i}
                            className={cx(
                              "px-4 py-3 text-[10.5px] font-bold text-zinc-500 uppercase tracking-[0.08em]",
                              col.right ? "text-right" : "text-left",
                              col.width,
                            )}
                          >
                            {col.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {filtered.map((c) => (
                        <DesktopRow key={c.id} c={c} />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile cards — same information, thumb-sized. */}
                <div className="md:hidden space-y-2">
                  {filtered.map((c) => (
                    <MobileRow key={c.id} c={c} />
                  ))}
                </div>
              </>
            )}

            <PromoDialog
              open={promoOpen}
              onClose={() => setPromoOpen(false)}
              recipients={filtered}
              audienceLabel={SEGMENTS.find((s) => s.key === segment)!.label}
            />
          </motion.div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}


function StateCell({ state }: { state: CustomerFollowUpState }) {
  const meta = STATE_META[state]
  return (
    <span className={cx("inline-flex items-center gap-2 text-[12.5px]", meta.text)}>
      <span className={cx("size-2 rounded-full shrink-0", meta.dot)} aria-hidden />
      {meta.label}
    </span>
  )
}

function DesktopRow({ c }: { c: CustomerSummary }) {
  const extraVehicles = c.vehicles.length - 1
  const navigate = useNavigate()
  return (
    // Whole row opens the customer — nobody should have to aim for the name.
    <tr
      onClick={() => navigate(`/customers/${c.id}`)}
      className="group cursor-pointer hover:bg-zinc-50/60 transition-colors duration-100"
    >
      <td className="px-4 py-3.5">
        <Link to={`/customers/${c.id}`} className="flex items-center gap-3 min-w-0">
          <div className="size-9 rounded-xl bg-zinc-100 grid place-items-center shrink-0">
            <span className="text-[11px] font-bold text-zinc-600">
              {initialsOf(c.name) || "??"}
            </span>
          </div>
          <div className="min-w-0">
            <p className="text-[13.5px] font-semibold text-zinc-900 truncate leading-tight">
              {c.name}
              {/* "Have we seen them before?" — answered without opening anything. */}
              {c.estimateCount >= 2 && (
                <span className="ml-1.5 align-middle inline-flex items-center rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 text-[9.5px] font-bold px-1 py-px uppercase tracking-wide">
                  Repeat
                </span>
              )}
            </p>
            <p className="text-[11.5px] text-zinc-400 truncate leading-tight mt-0.5 flex items-center gap-1">
              {c.phone ? (
                <>
                  <Phone01 className="size-3 shrink-0" aria-hidden />
                  {c.phone}
                </>
              ) : c.email ? (
                <>
                  <Mail01 className="size-3 shrink-0" aria-hidden />
                  <span className="truncate">{c.email}</span>
                </>
              ) : (
                "No contact info"
              )}
            </p>
          </div>
        </Link>
      </td>
      <td className="px-4 py-3.5 text-[12.5px] text-zinc-700 max-w-[220px]">
        <span className="truncate block">
          {c.vehicles[0] ?? "—"}
          {extraVehicles > 0 && (
            <span className="text-zinc-400"> +{extraVehicles} more</span>
          )}
        </span>
      </td>
      <td className="px-4 py-3.5">
        <StateCell state={c.followUp} />
      </td>
      <td className="px-4 py-3.5 text-[12px] text-zinc-500 tabular-nums whitespace-nowrap">
        {relTime(c.lastActivityAt)}
      </td>
      <td className="px-4 py-3.5 text-right whitespace-nowrap">
        <p className="text-[13.5px] font-bold text-zinc-900 tabular-nums leading-tight">
          {formatMoney(c.totalIdentified)}
        </p>
        <p className="text-[10.5px] text-zinc-400 leading-tight mt-0.5">
          {c.estimateCount} estimate{c.estimateCount === 1 ? "" : "s"}
        </p>
      </td>
      <td className="px-3 py-3.5">
        <Link
          to={`/customers/${c.id}`}
          aria-label={`Open ${c.name}`}
          className="grid place-items-center size-8 rounded-lg text-zinc-300 group-hover:text-zinc-500 hover:bg-zinc-100 transition-all"
        >
          <ChevronRight className="size-4" aria-hidden />
        </Link>
      </td>
    </tr>
  )
}

function MobileRow({ c }: { c: CustomerSummary }) {
  return (
    <Link
      to={`/customers/${c.id}`}
      className="flex items-center gap-3 rounded-2xl border border-zinc-200/70 bg-white px-3.5 py-3 active:scale-[0.99] transition-transform"
    >
      <div className="size-10 rounded-xl bg-zinc-100 grid place-items-center shrink-0">
        <span className="text-[12px] font-bold text-zinc-600">
          {initialsOf(c.name) || "??"}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold text-zinc-900 truncate leading-tight">
          {c.name}
        </p>
        <p className="text-[11.5px] text-zinc-400 truncate leading-tight mt-0.5">
          {c.vehicles[0] ?? "No vehicle"}
          {c.vehicles.length > 1 && ` +${c.vehicles.length - 1}`}
        </p>
        <div className="mt-1">
          <StateCell state={c.followUp} />
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-[13.5px] font-bold text-zinc-900 tabular-nums">
          {formatMoney(c.totalIdentified)}
        </p>
        <p className="text-[10.5px] text-zinc-400 mt-0.5">{relTime(c.lastActivityAt)}</p>
      </div>
    </Link>
  )
}

function Empty({ hasQuery }: { hasQuery: boolean }) {
  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-white p-10 text-center">
      <div className="size-11 rounded-2xl bg-zinc-100 grid place-items-center mx-auto mb-3">
        {/* User01 = a customer (a person) — Users01 stays the Team icon (#59). */}
        <User01 className="size-5 text-zinc-400" aria-hidden />
      </div>
      <p className="text-[14px] font-semibold text-zinc-900">
        {hasQuery ? "No matches" : "No customers yet"}
      </p>
      <p className="text-[13px] text-zinc-500 mt-1">
        {hasQuery
          ? "Try a different search, or switch back to All."
          : "Customers appear here as estimates are written."}
      </p>
    </div>
  )
}
